import type {
  ApplicationEditableFieldDto,
  ApplicationChangeEntryDto,
  ApplicationFieldOverrideDto,
} from '../api/admin';

/**
 * Presentation logic for reviewer field editing.
 *
 * Kept out of the components so the rules that matter — when an edit affordance
 * appears, what a value reads as, how a conflict is explained — are testable
 * without mounting MUI.
 */

export const MIN_REASON_DETAIL_LENGTH = 10;

/** Field groups in the order a reviewer reads them, with operator-facing headings. */
export const FIELD_GROUP_ORDER: ReadonlyArray<{ group: string; label: string; hint: string }> = [
  {
    group: 'agreement',
    label: 'Loan agreement identity',
    hint: 'What is printed on the agreement',
  },
  { group: 'loan', label: 'Loan request', hint: 'What they asked for' },
  {
    group: 'personal',
    label: 'Identity profile',
    hint: 'Feeds verification — not the agreement',
  },
  { group: 'employment', label: 'Employment', hint: 'Declared employer and role' },
];

export function groupEditableFields(
  fields: readonly ApplicationEditableFieldDto[],
): Array<{ group: string; label: string; hint: string; fields: ApplicationEditableFieldDto[] }> {
  const byGroup = new Map<string, ApplicationEditableFieldDto[]>();
  for (const field of fields) {
    const list = byGroup.get(field.group) ?? [];
    list.push(field);
    byGroup.set(field.group, list);
  }
  const out: Array<{
    group: string;
    label: string;
    hint: string;
    fields: ApplicationEditableFieldDto[];
  }> = [];
  for (const known of FIELD_GROUP_ORDER) {
    const group = byGroup.get(known.group);
    if (group?.length) {
      out.push({ ...known, fields: group });
      byGroup.delete(known.group);
    }
  }
  // A group the server knows about and this build does not still renders, rather
  // than silently disappearing from the UI.
  for (const [group, groupFields] of byGroup) {
    out.push({ group, label: group, hint: '', fields: groupFields });
  }
  return out;
}

/**
 * How a field's current value should read.
 *
 * Three states worth distinguishing, because collapsing them misleads:
 *   - a value
 *   - genuinely empty (nobody has supplied one)
 *   - unknown, because the owning service could not be reached
 */
export const REVIEWER_UNRESOLVED_FIELD_MESSAGE = 'We couldn’t confirm this field right now.';

export function formatFieldValue(field: ApplicationEditableFieldDto): string {
  if (field.sourceResolved === false) return REVIEWER_UNRESOLVED_FIELD_MESSAGE;
  return renderValue(field.currentValue, field.valueType);
}

const EMPLOYMENT_TYPE_LABELS: Readonly<Record<string, string>> = {
  FULL_TIME: 'Full time',
  PART_TIME: 'Part time',
  CONTRACT: 'Contract',
  SEASONAL: 'Seasonal',
  SELF_EMPLOYED: 'Self-employed',
  GIG: 'Gig / platform',
};

const WORK_ARRANGEMENT_LABELS: Readonly<Record<string, string>> = {
  ONSITE: 'On-site',
  REMOTE: 'Remote',
  HYBRID: 'Hybrid',
};

/** Human labels for employment enums on reviewer-facing surfaces. */
export function formatReviewerEmploymentFieldValue(fieldKey: string, value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (!normalized) return null;
  if (fieldKey === 'employment.employmentType') {
    return EMPLOYMENT_TYPE_LABELS[normalized] ?? null;
  }
  if (fieldKey === 'employment.workType') {
    return WORK_ARRANGEMENT_LABELS[normalized] ?? null;
  }
  return null;
}

export function renderValue(value: unknown, valueType?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (valueType === 'date' && typeof value === 'string') {
    const parsed = Date.parse(value.length === 10 ? `${value}T00:00:00Z` : value);
    if (Number.isFinite(parsed)) {
      return new Date(parsed).toLocaleDateString(undefined, { timeZone: 'UTC' });
    }
    return value;
  }
  if (valueType === 'number' && (typeof value === 'number' || typeof value === 'string')) {
    const n = Number(value);
    if (Number.isFinite(n)) return n.toLocaleString();
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Whether to show the pencil.
 *
 * Follows the server's `editable` flag rather than re-deriving it from status and
 * permissions. A client that worked it out for itself would eventually disagree
 * with the API and offer an affordance that then 403s.
 */
export function canEditField(field: ApplicationEditableFieldDto): boolean {
  return field.editable === true;
}

/**
 * Why a field cannot be edited, in words a reviewer can act on.
 *
 * The wording lives here, not on the server. The API returns a machine code and
 * whatever data the sentence needs; composing the sentence server-side would make
 * UI copy a backend concern, put it beyond translation, and mean a wording change
 * needed a backend deploy.
 *
 * `context` carries the extra facts a specific reason needs — the status, or who
 * owns the case.
 */
export function lockExplanation(
  field: ApplicationEditableFieldDto,
  context?: { status?: string; assignedToEmail?: string | null; assignedToUserId?: string | null },
): string | null {
  if (field.editable) return null;
  switch (field.notEditableReason) {
    case 'PERMISSION':
      return 'You do not have permission to edit this field.';
    case 'STATUS':
      return context?.status
        ? `Not editable while the application is ${humanStatus(context.status)}.`
        : 'Not editable at this stage of the application.';
    case 'ASSIGNMENT': {
      const who = context?.assignedToEmail?.trim() || null;
      return who
        ? `Assigned to ${who} — only they can edit this application.`
        : 'Assigned to another reviewer — only they can edit this application.';
    }
    case 'LOCKED':
      return 'Not editable in this release.';
    default:
      return 'Not editable.';
  }
}

/** `MANUAL_REVIEW` reads badly in a sentence; `manual review` does not. */
export function humanStatus(status: string): string {
  return status.replace(/_/g, ' ').toLowerCase();
}

/** Whether the value on screen is a reviewer correction rather than the source value. */
export function isOverridden(field: ApplicationEditableFieldDto): boolean {
  return field.overridden === true;
}

/**
 * The provenance line shown under an edited value.
 *
 * Names the person and the date, because "edited" without an author is a rumour.
 */
export function editedByLine(field: ApplicationEditableFieldDto): string | null {
  if (!field.lastChangedAt) return null;
  const who = field.lastChangedBy?.trim() || 'an operator';
  const when = new Date(field.lastChangedAt);
  if (!Number.isFinite(when.getTime())) return `Edited by ${who}`;
  return `Edited by ${who} on ${when.toLocaleString()}`;
}

/**
 * Whether a proposed value differs from the current one.
 *
 * Mirrors the server's no-op suppression so the dialog can disable Save rather
 * than round-tripping to be told nothing changed. Compared loosely on purpose:
 * metadata JSON holds numbers or their string form.
 */
export function isValueChanged(current: unknown, next: unknown): boolean {
  if (current === next) return false;
  if (current === null || current === undefined) return String(next ?? '') !== '';
  if (next === null || next === undefined) return true;
  if (typeof current === 'object' || typeof next === 'object') {
    return JSON.stringify(current) !== JSON.stringify(next);
  }
  const a = Number(current);
  const b = Number(next);
  if (Number.isFinite(a) && Number.isFinite(b)) return a !== b;
  return String(current).trim() !== String(next).trim();
}

export interface EditFormState {
  readonly value: string;
  readonly reasonCode: string;
  readonly reasonDetail: string;
}

export interface EditFormValidity {
  readonly canSubmit: boolean;
  /** Why Save is disabled, shown next to the button rather than hidden in a tooltip. */
  readonly blockedReason: string | null;
}

/**
 * Whether the edit dialog may submit.
 *
 * Every rule here is also enforced server-side; this exists so a reviewer learns
 * about a missing reason before typing a value they then lose.
 */
export function validateEditForm(
  field: ApplicationEditableFieldDto,
  form: EditFormState,
): EditFormValidity {
  const trimmedValue = form.value.trim();
  if (!trimmedValue) {
    return { canSubmit: false, blockedReason: 'Enter a value.' };
  }
  if (!isValueChanged(field.currentValue, coerceForCompare(trimmedValue, field.valueType))) {
    return { canSubmit: false, blockedReason: 'This is the same as the current value.' };
  }
  if (!form.reasonCode) {
    return { canSubmit: false, blockedReason: 'Choose why this is changing.' };
  }
  const detail = form.reasonDetail.trim();
  if (detail.length < MIN_REASON_DETAIL_LENGTH) {
    return {
      canSubmit: false,
      blockedReason: `Add a written reason of at least ${MIN_REASON_DETAIL_LENGTH} characters.`,
    };
  }
  return { canSubmit: true, blockedReason: null };
}

/** Numbers arrive from a text input; compare them as numbers or "5000" reads as changed. */
function coerceForCompare(raw: string, valueType: string): unknown {
  if (valueType === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  }
  return raw;
}

/** The initial dialog value, pre-filled with what is there now. */
export function initialEditValue(field: ApplicationEditableFieldDto): string {
  const value = field.currentValue;
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Turn a save failure into something a reviewer can act on.
 *
 * A 409 is the interesting case and has two distinct causes that need different
 * responses, so they are not collapsed into "conflict".
 */
export function editErrorMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const serverMessage = (
    error as { response?: { data?: { message?: string; error?: string } } }
  )?.response?.data;
  const message =
    serverMessage?.message?.trim() || serverMessage?.error?.trim() || '';

  if (status === 409) {
    return message || 'Someone else changed this first. Reload to see their change before applying yours.';
  }
  if (status === 403) {
    return message || 'You do not have permission to make this change.';
  }
  if (status === 400) {
    return message || 'That value was rejected. Check the format and try again.';
  }
  return message || 'The change could not be saved.';
}

/** True when the failure means the reviewer must reload before retrying. */
export function requiresReload(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 409;
}

// ── change history ────────────────────────────────────────────────────────

/** Operator-facing label for a change-log action. */
export function changeActionLabel(action: ApplicationChangeEntryDto['action']): string {
  switch (action) {
    case 'UPDATE':
      return 'Changed';
    case 'OVERRIDE_SET':
      return 'Corrected';
    case 'OVERRIDE_CLEAR':
      return 'Correction withdrawn';
    case 'DECISION':
      return 'Decision';
    case 'REOPEN':
      return 'Reopened';
    default:
      return action;
  }
}

/**
 * Whether an entry describes a value we could not read at the time.
 *
 * Rendered explicitly, because a blank before-value that actually means "the
 * employer service was down" would otherwise read as "the field was empty".
 */
export function changeSourceWasUnreadable(entry: ApplicationChangeEntryDto): string | null {
  for (const blob of [entry.beforeJson, entry.afterJson]) {
    if (blob && typeof blob === 'object' && (blob as { unresolved?: unknown }).unresolved) {
      const reason = (blob as { reason?: unknown }).reason;
      return typeof reason === 'string' ? reason : 'The source value could not be read.';
    }
  }
  return null;
}

/**
 * Group history into change-sets.
 *
 * One reviewer act can touch several fields, and listing them as separate rows
 * would make one correction look like five.
 */
export function groupChangesByChangeSet(
  entries: readonly ApplicationChangeEntryDto[],
): Array<{
  changeSetId: string;
  at: string;
  actor: string;
  reasonLabel: string;
  reasonDetail: string;
  entries: ApplicationChangeEntryDto[];
}> {
  const order: string[] = [];
  const groups = new Map<string, ApplicationChangeEntryDto[]>();
  for (const entry of entries) {
    if (!groups.has(entry.changeSetId)) {
      groups.set(entry.changeSetId, []);
      order.push(entry.changeSetId);
    }
    groups.get(entry.changeSetId)!.push(entry);
  }
  return order.map((changeSetId) => {
    const group = groups.get(changeSetId)!;
    const first = group[0]!;
    return {
      changeSetId,
      at: first.createdAt,
      actor: first.actorEmail?.trim() || first.actorUserId,
      reasonLabel: first.reasonLabel,
      reasonDetail: first.reasonDetail,
      entries: group,
    };
  });
}

/** Live corrections keyed by field, for rendering markers outside the edit panel. */
export function overridesByFieldKey(
  overrides: readonly ApplicationFieldOverrideDto[] | undefined,
): ReadonlyMap<string, ApplicationFieldOverrideDto> {
  return new Map((overrides ?? []).map((o) => [o.fieldKey, o]));
}
